import { showErrorToast, showSuccessToast } 
  from "../components/toasts/ToastNotification";

/* =========================================================
   CONSTANTS
========================================================= */
const MAX_CANVAS_PIXELS = 16000;

/* =========================================================
   UTF-8 SAFE SVG → DATA URL
========================================================= */
function svgToDataUrl(svgString) {
  const encoded = encodeURIComponent(svgString)
    .replace(/'/g, "%27")
    .replace(/"/g, "%22");
  return "data:image/svg+xml;charset=utf-8," + encoded;
}

/* =========================================================
   SCALE SAFETY
========================================================= */
function getSafeScale(width, height, desiredScale) {
  const maxScaleX = MAX_CANVAS_PIXELS / width;
  const maxScaleY = MAX_CANVAS_PIXELS / height;
  return Math.min(desiredScale, maxScaleX, maxScaleY);
}

function isCanvasTooLarge(width, height, scale) {
  return (
    width * scale > MAX_CANVAS_PIXELS ||
    height * scale > MAX_CANVAS_PIXELS
  );
}

/* =========================================================
   PREPARE SVG FOR EXPORT
========================================================= */
function prepareSvgForExport(svg, viewBox, width, height) {
  const cloned = svg.cloneNode(true);

  cloned.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  cloned.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

  cloned.setAttribute("width", width);
  cloned.setAttribute("height", height);
  cloned.setAttribute("viewBox", viewBox);

  cloned.setAttribute("shape-rendering", "geometricPrecision");
  cloned.setAttribute("text-rendering", "geometricPrecision");
  cloned.setAttribute("image-rendering", "optimizeQuality");

  cloned.querySelectorAll("text").forEach((t) => {
    t.setAttribute("font-family", "Inter, Arial, sans-serif");
    t.setAttribute("paint-order", "stroke fill");
  });

  return cloned;
}

/* =========================================================
   SVG → PNG EXPORT (ULTRA HD, SAFE)
========================================================= */
async function exportSvgToPng(svg, width, height, fileName) {
  const DPR = window.devicePixelRatio || 2;
  const DESIRED_SCALE = DPR * 4;

  const FINAL_SCALE = getSafeScale(width, height, DESIRED_SCALE);

  if (isCanvasTooLarge(width, height, FINAL_SCALE)) {
    showErrorToast(
      "Diagram is too large. Zoom in or export SVG instead."
    );
    return;
  }

  const xml = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = svgToDataUrl(xml);

  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = rej;
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.floor(width * FINAL_SCALE);
  canvas.height = Math.floor(height * FINAL_SCALE);

  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.setTransform(FINAL_SCALE, 0, 0, FINAL_SCALE, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(img, 0, 0, width, height);

  const link = document.createElement("a");
  link.download = fileName;
  link.href = canvas.toDataURL("image/png", 1.0);
  link.click();

  showSuccessToast("Diagram exported successfully 🚀");
}

/* =========================================================
   MAIN EXPORT FUNCTION
========================================================= */
export async function exportDiagram(
  svgElement,
  nodes,
  projectId,
  computeNodesBounds,
  viewBox,
  projectName
) {
  try {
    if (!svgElement || !nodes?.length) {
      throw new Error("No diagram found");
    }

    const bounds = computeNodesBounds(nodes);
    if (!bounds) throw new Error("Invalid diagram bounds");

    const diagramWidth = bounds.maxX - bounds.minX;
    const diagramHeight = bounds.maxY - bounds.minY;

    const svgRect = svgElement.getBoundingClientRect();

    const isSmallDiagram =
      diagramWidth <= svgRect.width &&
      diagramHeight <= svgRect.height;

    let vx, vy, vw, vh;

    if (isSmallDiagram) {
      vx = bounds.minX;
      vy = bounds.minY;
      vw = diagramWidth;
      vh = diagramHeight;
    } else {
      const pt1 = svgElement.createSVGPoint();
      pt1.x = svgRect.left;
      pt1.y = svgRect.top;

      const pt2 = svgElement.createSVGPoint();
      pt2.x = svgRect.right;
      pt2.y = svgRect.bottom;

      const ctmRaw = svgElement.getScreenCTM();
      if (!ctmRaw) throw new Error("SVG not ready");

      const ctm = ctmRaw.inverse();
      const svgPt1 = pt1.matrixTransform(ctm);
      const svgPt2 = pt2.matrixTransform(ctm);

      vx = svgPt1.x;
      vy = svgPt1.y;
      vw = svgPt2.x - svgPt1.x;
      vh = svgPt2.y - svgPt1.y;
    }

    if (!vw || !vh || vw <= 0 || vh <= 0) {
      throw new Error("Invalid export size");
    }

    const exportSvg = prepareSvgForExport(
      svgElement,
      `${vx} ${vy} ${vw} ${vh}`,
      vw,
      vh
    );

    await exportSvgToPng(
      exportSvg,
      vw,
      vh,
      `PertDiagram_${projectName || projectId || "diagram"}.png`
    );
  } catch (err) {
    console.error("EXPORT ERROR:", err);
    showErrorToast(err.message || "PNG export failed");
  }
}
